import {ellipsis} from 'polished';
import React, {FC} from 'react';
import styled, {css} from 'styled-components';

import {useToggle} from '../../../../../../libs/shared/helpers/src/react/hooks/hookHelpers';
import {Icon} from '../../../../../../libs/shared/ui/src/components/icon/icon';
import {PopoverCoordinator} from '../../../../../../libs/shared/ui/src/components/popovers/popoverCoordinator';
import {TooltipCoordinator} from '../../../../../../libs/shared/ui/src/components/tooltips/tooltipCoordinator';
import {TooltipOverflowCondition} from '../../../../../../libs/shared/ui/src/components/tooltips/tooltipOverflowCondition';
import {fontSizes, fontWeights} from '../../../../../../libs/shared/ui/src/styles/fontStyles';
import {useAppDispatch} from '../../../../../helpers/reduxHelpers';
import {ApplicationModel} from '../../../../../models/api/applicationModel';
import {ConversationModel} from '../../../../../models/api/conversationModel';
import {TopicModel} from '../../../../../models/api/topicModel';
import {removeTopicFromConversationsAction} from '../../../../../store/data/conversations/topicConversationsActions';
import {ViewerPanelId} from '../../../../../store/panels/panelStateBase';
import {ConversationSummaryContainer} from '../../conversation/conversationSummary/conversationSummaryContainer';
import {ConversationSummarySectionsEnum} from '../../conversation/conversationSummary/conversationSummarySections';
import {TopicDropdownContainer} from '../../conversation/header/topic/topicDropdownContainer';

/*
 * Props.
 */

interface ActivityReferencePreviewerPillProps {
  panelId: ViewerPanelId;
  conversationId: number;
  teammateId: number;
  conversation?: ConversationModel;
  topic?: TopicModel;
  application?: ApplicationModel;
}

/*
 * Style.
 */

interface StyledPillDivProps {
  $hasHover: boolean;
}

const StyledPillDiv = styled.div<StyledPillDivProps>`
  ${ellipsis('200px')};
  display: inline-flex;
  flex-flow: row;
  gap: 4px;
  margin-top: 1px;

  max-width: 200px;
  padding: 1px 3px;
  margin-bottom: -3px;

  background: ${(p) => p.theme.greys.shade10};
  border: 1px solid ${(p) => p.theme.greys.shade70};
  border-radius: 2px;

  font-size: ${fontSizes.veryTiny};
  font-weight: ${fontWeights.semibold};

  ${(p) =>
    p.$hasHover &&
    css`
      background: ${p.theme.greys.shade30};
    `}
  &:hover {
    background: ${(p) => p.theme.greys.shade30};
  }
`;

const StyledNameWrapperDiv = styled.div`
  display: inline;
  color: ${(p) => p.theme.palette.blue.shade40};
`;

const StyledApplicationWrapperDiv = styled.div`
  display: flex;
  flex-flow: column;
  align-items: center;
`;

const StyledApplicationImg = styled.img`
  width: 12px;
  height: 12px;
`;

/*
 * Component.
 */

export const ActivityReferencePreviewerPill: FC<ActivityReferencePreviewerPillProps> = (props) => {
  const {panelId, conversationId, teammateId, topic, application, conversation} = props;
  const [isConversationSummaryOpen, toggleIsConversationSummaryOpen] = useToggle();
  const dispatch = useAppDispatch();

  if (!topic || !conversation) {
    return null;
  }

  const removeTopic = () => {
    dispatch(removeTopicFromConversationsAction([conversationId], topic.id));
  };

  const maybeRenderConversationSummary = () =>
    isConversationSummaryOpen ? (
      <ConversationSummaryContainer
        conversation={conversation}
        teammateId={teammateId}
        onRequestClose={toggleIsConversationSummaryOpen}
        initiallyExpandedSection={ConversationSummarySectionsEnum.REFERENCE_PREVIEWERS}
        panelId={panelId}
        selectedTopicId={topic.id}
      />
    ) : null;

  return (
    <>
      <PopoverCoordinator
        renderButton={(hasHover) => (
          <StyledPillDiv $hasHover={hasHover}>
            {renderIcon(application)}
            <TooltipCoordinator render={() => topic.name}>
              <TooltipOverflowCondition>
                <StyledNameWrapperDiv>{topic.name}</StyledNameWrapperDiv>
              </TooltipOverflowCondition>
            </TooltipCoordinator>
          </StyledPillDiv>
        )}
        renderModal={(popoverProps) => (
          <TopicDropdownContainer
            {...props}
            {...popoverProps}
            topic={topic}
            removeTopic={conversation.topicIds.includes(topic.id) ? removeTopic : undefined}
            isApplicationTopic
            onToggleSummary={toggleIsConversationSummaryOpen}
          />
        )}
      />
      {maybeRenderConversationSummary()}
    </>
  );
};

/*
 * Helpers.
 */

function renderIcon(application?: ApplicationModel) {
  if (application && application.iconUrl) {
    return (
      <StyledApplicationWrapperDiv>
        <StyledApplicationImg src={application.iconUrl} />
      </StyledApplicationWrapperDiv>
    );
  }
  return <Icon name="topicWebSmall" size={12} />;
}
